'use client';

import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/cn';
import { forwardRef, useRef } from 'react';
import type { DateValue } from 'react-aria';
import { useButton, useDatePicker } from 'react-aria';
import type { DatePickerStateOptions } from 'react-stately';
import { useDatePickerState } from 'react-stately';
import { Calendar } from './calendar';
import { DateField, type AdditionalDateFieldProps } from './date-field';
import { useForwardedRef } from './hooks/use-forward-ref';
import { Icons } from '@/components/icons';

const DateTimePicker = forwardRef<
  HTMLDivElement,
  DatePickerStateOptions<DateValue> & AdditionalDateFieldProps
>((props, forwardedRef) => {
  const ref = useForwardedRef(forwardedRef);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const state = useDatePickerState(props);
  const {
    groupProps,
    fieldProps,
    buttonProps: _buttonProps,
    dialogProps,
    calendarProps,
  } = useDatePicker(
    {
      ...props,
      label: 'Datum',
    },
    state,
    ref
  );

  const { buttonProps } = useButton(_buttonProps, buttonRef);
  return (
    <div
      {...groupProps}
      ref={ref}
      data-invalid={state.isInvalid || props.isInvalid || undefined}
      className={cn(
        groupProps.className,
        'group/datepicker flex w-full items-center rounded-input bg-surface ring-offset-background transition-colors focus-within:ring-[3px] focus-within:ring-ring/50 data-[invalid=true]:focus-within:ring-destructive/20'
      )}
    >
      <DateField
        label='Datum'
        {...fieldProps}
        shouldForceLeadingZeros={true}
        hideResetButton={props.hideResetButton}
      />
      <Popover open={state.isOpen} modal>
        <PopoverTrigger asChild>
          <Button
            {...buttonProps}
            variant='outline'
            className='h-11 rounded-l-none rounded-r-input bg-surface shadow-none text-muted-foreground transition-colors group-focus-within/datepicker:border-ring group-data-[invalid=true]/datepicker:border-destructive group-data-[invalid=true]/field:border-destructive hover:text-foreground'
            disabled={props.isDisabled}
          >
            <Icons.calendar className='size-5' />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          onInteractOutside={() => state.setOpen(false)}
          className='w-full'
        >
          <div {...dialogProps} className='space-y-3'>
            <Calendar {...calendarProps} datePickerState={state} />
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
});

DateTimePicker.displayName = 'DateTimePicker';

export { DateTimePicker };
